/* eslint-disable no-shadow */
/* eslint-disable no-param-reassign */
/* eslint-disable no-return-await */
import { useAuth0 } from '@auth0/auth0-react'
import CheckBox from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank'
import Delete from '@mui/icons-material/Delete'
import UploadFile from '@mui/icons-material/UploadFile'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import CircleIcon from '@mui/icons-material/Circle'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import DoneIcon from '@mui/icons-material/Done'
import {
  ButtonGroup,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import AppContext from '../../context/appContext'
import { useLockedPaddocks } from '../../hooks/data/useLockedPaddocks'
import { queryKeys } from '../../hooks/data/queryKeys'
import {
  useCreateFarmSubdivision,
  useDeleteFarmSubdivision,
  useUpdateFarmSubdivision,
  useUploadSubdivisionTemplate,
} from '../../hooks/data/useFarmSubdivisions'
import { useMonitoringSites } from '../../hooks/data/useMonitoringSites'
import {
  useCreatePaddocks,
  useDeletePaddock,
  useUpdatePaddocks,
} from '../../hooks/data/usePaddocks'
import { usePartialImportKml } from '../../hooks/usePartialImportKml'
import { useUserRole } from '../../hooks/useUserRole'
import getFarmSubdivisions from '../../services/farmMapping/getFarmSubdivision'
import { getPaddocks } from '../../services/farmMapping/getPaddocks'
import { errors } from '../../services/ruutsApi/errors'
import { getMetricsByPaddockId } from '../../services/ruutsApi/getMetricsByPaddockId'
import { LAND_PLAN_YEAR, PROGRAM_IDS } from '../../utils/constants'
import {
  capturePaddockGeometrySnapshot,
  findLockedPaddocksTouchedByFullEditSave,
  partitionPaddocksForFullEditSave,
} from '../../utils/farmSubdivisions/fullEditLockGuard'
import { normalizePaddockName } from '../../utils/farmSubdivisions/normalizePaddockName'
import { getServerConflictFeatureNames } from '../../utils/farmSubdivisions/partialImportConflict'
import { getKmlImportErrorMessage } from '../../utils/Files/kmlImportErrors'
import { GeoJSON } from '../../utils/GeoJSON/index'
import { processLoading } from '../../utils/Loading/processLoading'
import {
  checkIntersectionWithOtherFeatures,
  checkIntersectionWithPerimeter,
} from '../../utils/mapDrawHandle'
import { messages } from '../../utils/messages/index'
import { isPartialImportEnabled } from '../../utils/programConfig/partialImport'
import CustomAlert from '../Alert/CustomAlert'
import { EditionButton } from '../Button/Button'
import { DeleteIconButton } from '../Button/DeleteIconButton'
import { EditIconButton } from '../Button/EditIconButton'
import { LoadingButton } from '../Button/LoadingButton'
import { ModalTypes } from '../GlobalConfirmationModal/GlobalConfirmationModal'
import { displayErrorMessageModes } from '../GlobalLoadingModal/GlobalLoadingModal'
import { mapActions } from './AllotmentMap'
import BaseFarmSubdivisionYearSelect from './BaseFarmSubdivisionYearSelect'
import YearSelector from './YearSelector'

// Module scope so every setServerConflict(noServerConflict) hits React's Object.is bail-out — a
// per-render literal would re-render all consumers on every "clear".
const noServerConflict = { stagedIds: [], names: [] }

// A VALUE identity, not an array reference: the server lists are query results whose identity
// changes on every refetch, and re-seeding the working set on identity would silently discard
// in-progress renames, isInProject toggles and draw edits.
const workingSetSignature = farmSubdivision =>
  `${farmSubdivision?.id ?? ''}:${(farmSubdivision?.paddockIds ?? []).join(',')}`

// The loteo, paddock and farm reads resolve independently: marking the loteo as seeded while the
// paddock read is still in flight would empty the working set for good, since the later arrival
// carries the very same signature and would be read as "already seeded".
const canSeedWorkingSet = ({ farm, paddockIds, resolvedPaddocks }) =>
  !!farm && (paddockIds.length === 0 || resolvedPaddocks.length > 0)

const AllotmentPanel = ({
  farmSubdivisions,
  yearsAllowed,
  paddocks,
  paddocksFeatures,
  setPaddocksFeatures,
  farm,
  wasSaved,
  setWasSaved,
  drawActivated,
  setDrawActivated,
  setMapAction,
  mapAction,
  setSelectedArea,
  selectedArea,
  selectedYears,
  setSelectedYears,
  setSelectedAreaType,
  formDefinitions,
  stagedImportFeatures,
  setStagedImportFeatures,
}) => {
  const [reallotment, setReallotment] = useState(false)
  const [savedYears, setSavedYears] = useState([])
  const [selectedFarmSubdivision, setSelectedFarmSubdivision] = useState()
  const [baseFarmSubdivisionYear, setBaseFarmSubdivisionYear] = useState('')
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  // These five only ever run in sequence inside saveAllotment/updateAllotment, whose finally block
  // already busts a superset of what each one would bust on its own.
  const { mutateAsync: createPaddocks } = useCreatePaddocks({ skipInvalidation: true })
  const { mutateAsync: updatePaddocks } = useUpdatePaddocks({ skipInvalidation: true })
  const { mutateAsync: deletePaddock } = useDeletePaddock({ skipInvalidation: true })
  const { mutateAsync: createFarmSubdivision } = useCreateFarmSubdivision({
    skipInvalidation: true,
  })
  const { mutateAsync: updateSubdivision } = useUpdateFarmSubdivision({ skipInvalidation: true })
  const { mutateAsync: deleteSubdivision } = useDeleteFarmSubdivision()
  const { mutateAsync: uploadSubdivisionTemplate } = useUploadSubdivisionTemplate()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const [allSelected, setAllSelected] = useState(false)
  // partialChanges (AppContext) becomes true the instant a reallotment session opens, regardless
  // of any real edit — correct for Volver's broad "session in progress" warning, but useless as an
  // import-confirmation gate (it would fire on every single import). This tracks only genuine
  // paddock mutations (rename, project-inclusion toggle, geometry draw/edit/delete) within the
  // current session, so the KML-import guard only interrupts when there is real work to lose.
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false)
  // Baseline for the lock guard's geometry-change check, taken at the moment reallotment opens
  // (paddocksFeatures already reflects the load-pass perimeter/neighbor-clip normalization by
  // then, since that runs while merely viewing the loteo, before "Editar Loteo" is clicked). The
  // backend enforces locks authoritatively regardless — this only decides when the FE UX guard
  // should interrupt a save, so it must compare against what the USER'S SESSION started from, not
  // the raw fetch, which the normalization pass legitimately differs from with zero user action.
  const [paddockGeometrySnapshot, setPaddockGeometrySnapshot] = useState({})
  // Lotes the backend refused to import because they overlap paddocks it saw inside its own
  // transaction — paddocks this session's snapshot may predate. Held separately from the hook's
  // geometric conflict detection (which only ever sees the session's own working set) because only
  // a re-read of the current paddocks can resolve them.
  //
  // Tracked by stagedId, never by name: the conflict is geometric, so a rename resolves nothing and
  // the verdict must survive it (with the warning following the new name), while removing the lote
  // resolves it outright — that geometry will never be uploaded. Names are kept only as a fallback
  // for the case where the server names no lote we can match, so the recovery action stays reachable
  // instead of vanishing.
  const [serverConflict, setServerConflict] = useState(noServerConflict)

  const serverConflictNames = useMemo(() => {
    if (!serverConflict.stagedIds.length) return serverConflict.names
    const conflictingIds = new Set(serverConflict.stagedIds)
    return stagedImportFeatures
      .filter(staged => conflictingIds.has(staged.properties.stagedId))
      .map(staged => staged.properties.name)
  }, [serverConflict, stagedImportFeatures])

  const {
    userRoleSelected,
    partialChanges,
    setPartialChanges,
    loadingModalConfig,
    currentFarm,
    setLoadingModalConfig,
    setConfirmationModalConfig,
    programConfig,
  } = useContext(AppContext)

  // A genuine paddock mutation raises both dirty flags at once — see the hasUnsavedEdits
  // declaration above for why the two flags deliberately differ.
  const markUnsavedEdit = useCallback(() => {
    setPartialChanges(true)
    setHasUnsavedEdits(true)
  }, [setPartialChanges])

  const { isViewOnly, isAdmin, isGISTechnician } = useUserRole()
  const { data: monitoringSites } = useMonitoringSites({
    farmId: currentFarm?.id,
  })

  const hasRandomMonitoringSites = useMemo(
    () => monitoringSites && monitoringSites.some(site => site.isRandomSite && !site.isDeleted),
    [monitoringSites],
  )

  // Locking only applies to the full-edit (reallotment) flow — saveAllotment (create) always
  // sends brand-new paddocks with no ids, never consults lock data, and selectedFarmSubdivision
  // is {} there (year undefined), which would otherwise fetch every paddocks_usage MetricEvent
  // across ALL years of the farm instead of none. Withholding farmId keeps the query disabled.
  const {
    lockedPaddockIds = [],
    isLoading: isLoadingLocks,
    isError: isLockLookupFailed,
  } = useLockedPaddocks({
    farmId: reallotment ? currentFarm?.id : undefined,
    year: selectedFarmSubdivision?.year,
  })

  const partialImportEnabled = isPartialImportEnabled(programConfig)

  const {
    stageKmlFile,
    renameStagedFeature,
    removeStagedFeature,
    clipStagedFeatures,
    confirmImport,
    discardStagedImport,
    isImporting,
    conflictingFeatureNames,
    hasConflict,
    duplicateStagedIds,
    hasDuplicateGeometries,
  } = usePartialImportKml({
    farmSubdivisionId: selectedFarmSubdivision?.id,
    existingFeatures: paddocksFeatures,
    stagedFeatures: stagedImportFeatures,
    setStagedFeatures: setStagedImportFeatures,
  })

  const hasStagedImport = stagedImportFeatures.length > 0
  const hasServerConflict = serverConflictNames.length > 0

  // drawActivated is deliberately not folded in: it gates the view-mode derivation outright, while
  // the create/edit derivation runs with it on.
  const hasSessionWorkToLose = hasUnsavedEdits || hasStagedImport

  const seededWorkingSetRef = useRef(null)
  const previousBaseYearRef = useRef('')

  // Emptying the working set must also drop the seed marker, or re-selecting the very same loteo
  // would be read as "already seeded" and leave the panel permanently empty.
  const clearWorkingSet = useCallback(() => {
    seededWorkingSetRef.current = null
    setPaddocksFeatures([])
  }, [setPaddocksFeatures])

  // Existing names are normalized to the form the backend stores them in, which is the form staged
  // names already carry — so an "is this name taken?" comparison is apples to apples.
  //
  // PERSISTED names (paddocks still in the subdivision) always count, not just the working set:
  // the import's own reload (finishImport → reloadInformation → resetControls) discards local
  // renames/deletes, so the DB still holds the original name and the backend duplicate rejection
  // (which carries no names) would abort the whole atomic batch behind the generic snackbar.
  // Unioned with the working set so intra-session names — new drawings, local renames — collide too.
  const existingPaddockNames = useMemo(() => {
    const persistedIdSet = new Set(selectedFarmSubdivision?.paddockIds ?? [])
    const persistedNames = paddocks
      .filter(paddock => persistedIdSet.has(paddock.id))
      .map(paddock => normalizePaddockName(paddock.toGeoJSON?.properties?.name ?? ''))
    const workingNames = paddocksFeatures.map(paddock =>
      normalizePaddockName(paddock.properties.name ?? ''),
    )
    return new Set([...persistedNames, ...workingNames])
  }, [paddocks, selectedFarmSubdivision?.paddockIds, paddocksFeatures])

  // The backend aborts the whole atomic upload over an unnamed placemark, an in-batch duplicate, or
  // a name that already exists in the subdivision — taking every valid lote in the file with it, and
  // (for the last two) reporting no names at all. Block the confirm client-side instead, where every
  // offending name sits in an editable field. The confirm gate and the per-row error highlight must
  // decide "invalid" from this one set: two implementations of the rule drift into a disabled button
  // with no field marked.
  const invalidStagedIds = useMemo(() => {
    const names = stagedImportFeatures.map(staged => staged.properties.name?.trim())
    const duplicatedNames = new Set(names.filter((name, index) => names.indexOf(name) !== index))
    return new Set(
      stagedImportFeatures
        .filter(
          (staged, index) =>
            !names[index] ||
            duplicatedNames.has(names[index]) ||
            existingPaddockNames.has(names[index]),
        )
        .map(staged => staged.properties.stagedId),
    )
  }, [stagedImportFeatures, existingPaddockNames])

  const hasInvalidStagedNames = invalidStagedIds.size > 0

  const duplicateStagedIdSet = useMemo(() => new Set(duplicateStagedIds), [duplicateStagedIds])

  // The lockedPaddockIds array itself still feeds the save partition/guard helpers; this Set only
  // serves the per-row lookups, which run several times per paddock on every render. Both sides of
  // the membership check fold to lowercase — the lock lookup mirrors the backend's folding, while
  // paddock rows can carry legacy casings.
  const lockedPaddockIdSet = useMemo(
    () => new Set(lockedPaddockIds.map(id => id.toLowerCase())),
    [lockedPaddockIds],
  )

  // Scoped to reallotment defense-in-depth — even a stale/leaked lockedPaddockIds value must never
  // restrict the create flow, which has nothing to do with locking.
  const isPaddockLocked = useCallback(
    paddock => reallotment && lockedPaddockIdSet.has(paddock?.properties?.id?.toLowerCase()),
    [reallotment, lockedPaddockIdSet],
  )

  // A loading or failed lock lookup already blocks the save, so leaving editing live would let
  // the user build up work they can never persist (the only exit being Volver, which discards
  // everything). Scoped to reallotment because the create flow never consults lock data — its
  // disabled query must not freeze loteo creation.
  const isEditingBlocked = isImporting || (reallotment && (isLoadingLocks || isLockLookupFailed))

  const isEditDisabled =
    isAdmin || isGISTechnician ? false : !selectedFarmSubdivision?.allowEdition?.value || isViewOnly

  // A greyed-out button with an empty tooltip leaves the user with no way to find out what to do:
  // the permission-driven block (Analyst, GIS Technician with blocked periods) carries no backend
  // reason at all, so it needs its own.
  const editDisabledReason =
    selectedFarmSubdivision?.allowEdition?.reason ||
    (isViewOnly ? messages.getMessage(messages.keys.NO_PERMISSIONS_GRANTED) : null)

  // The load-pass normalization: clip every paddock against the farm perimeter and against its
  // already-processed neighbours. Pure (it only reads farm), so callers that need the normalized
  // features themselves — and not just the state write — can compute them without waiting for a
  // re-render.
  const normalizePaddockFeatures = useCallback(
    pF => {
      let newFeatures = []
      pF.forEach(paddock => {
        const p = checkIntersectionWithPerimeter(paddock, farm.toGeoJSON)
        newFeatures = checkIntersectionWithOtherFeatures(
          p,
          newFeatures,
          false,
          undefined,
          enqueueSnackbar,
          closeSnackbar,
        )
        p.properties = paddock.properties
        p.properties.area = GeoJSON.hectareArea(p)
        p.metrics = paddock.metrics
        newFeatures = [...newFeatures, p]
      })
      return newFeatures.sort((a, b) => b.properties.area - a.properties.area)
    },
    [farm, enqueueSnackbar, closeSnackbar],
  )

  const handleIntersections = useCallback(
    pF => {
      if (!farm) return
      setPaddocksFeatures(normalizePaddockFeatures(pF))
    },
    [farm, normalizePaddockFeatures, setPaddocksFeatures],
  )

  const handleSelectAll = () => {
    const newValue = !allSelected
    setAllSelected(newValue)
    // A locked paddock is dropped from the update batch by partitionPaddocksForFullEditSave, so
    // toggling it here would show as saved and then silently revert on the next load.
    setPaddocksFeatures(prev =>
      prev.map(p =>
        isPaddockLocked(p) ? p : { ...p, properties: { ...p.properties, isInProject: newValue } },
      ),
    )
    markUnsavedEdit()
  }

  useEffect(() => {
    if (selectedYears.length === 1 && selectedYears[0] === LAND_PLAN_YEAR) {
      // handleSelectAll skips locked paddocks, so their (untouchable) isInProject value must not
      // count against "everything is selected" — otherwise the label could never flip once a
      // locked paddock happens to be unchecked. .every() on an empty array is vacuously true, so
      // a loteo where every paddock is locked must not report "everything selected" either —
      // select-all would have nothing left to act on.
      const unlockedPaddocksFeatures = paddocksFeatures.filter(paddock => !isPaddockLocked(paddock))
      const allChecked =
        unlockedPaddocksFeatures.length > 0 &&
        unlockedPaddocksFeatures.every(({ properties }) => properties.isInProject || false)
      setAllSelected(allChecked)
    }
  }, [selectedYears, paddocksFeatures, isPaddockLocked])

  const baseFileName = useMemo(() => {
    const year = baseFarmSubdivisionYear || selectedYears[0]
    return farmSubdivisions.find(s => s.year === year)?.templateFileName || ''
  }, [baseFarmSubdivisionYear, farmSubdivisions, selectedYears])

  const resetControls = () => {
    clearWorkingSet()
    setSelectedYears([])
    setSelectedFarmSubdivision({})
    setBaseFarmSubdivisionYear('')
    setDrawActivated(false)
    setReallotment(false)
    setWasSaved(true)
    setMapAction(null)
    setPartialChanges(false)
    setHasUnsavedEdits(false)
    setAllSelected(false)
    setPaddockGeometrySnapshot({})
    setServerConflict(noServerConflict)
    discardStagedImport()
  }

  // Shared shape for "this action would discard something — ask first" flows. Callers each pass
  // the flag that precisely matches WHAT they are about to discard: handleBack uses the broad
  // partialChanges (any active session), handleImportKml uses the precise hasUnsavedEdits (real
  // paddock mutations only) — gating import on the broad flag would ask on every single import,
  // since partialChanges is already true the instant a reallotment session opens.
  const confirmIfDirty = ({ isDirty, message, onConfirm }) => {
    if (!isDirty) {
      onConfirm()
      return
    }
    setConfirmationModalConfig({
      open: true,
      title: 'Cambios sin guardar',
      message,
      confirmLabel: 'Continuar',
      cancelLabel: 'Cancelar',
      confirmAction: onConfirm,
    })
  }

  // Must stay a superset of what the mutation hooks bust on success: the callers below are the
  // settled paths, which run precisely when no onSuccess did.
  const invalidateAllotmentReads = useCallback(
    (options = {}) =>
      Promise.all(
        [
          queryKeys.paddocks(farm?.id),
          queryKeys.holisticPlanIssuesRoot(farm?.id),
          queryKeys.paddocksByIdsRoot,
          queryKeys.farmSubdivisionsRoot(farm?.id),
          // The partial KML import writes paddocks outside the mutation hooks, so this is the only
          // thing that refreshes the QU derivation for that flow.
          queryKeys.quantificationUnitsRoot(farm?.id),
          queryKeys.quHintsRoot(farm?.id),
          queryKeys.projectArea(farm?.id),
        ].map(queryKey => queryClient.invalidateQueries({ queryKey }, options)),
      ),
    [queryClient, farm?.id],
  )

  // resetControls stays part of the reload: it also discards any staged import, which no cache
  // refresh would do. throwOnError because invalidateQueries otherwise swallows every rejection and
  // the caller would announce a success it cannot vouch for.
  const reloadInformation = async () => {
    await invalidateAllotmentReads({ throwOnError: true })
    resetControls()
  }

  const saveAllotment = async () => {
    await processLoading({
      setLoadingModalConfig,
      loadingMessage: 'Generando lote...',
      doAction: async () => {
        const paddocksToCreate = paddocksFeatures.map(paddock => {
          return {
            name: paddock.properties.name,
            geometry: paddock.geometry,
            farmId: farm.id,
            isInProject: paddock.properties.isInProject,
            createdBy: user.email,
            updatedBy: user.email,
          }
        })

        try {
          const farmSubdivisionsToCreateBySelectedYears = await createPaddocks({
            farm,
            years: selectedYears,
            paddocks: paddocksToCreate,
          })
          await Promise.all(
            farmSubdivisionsToCreateBySelectedYears.map(async farmSubdivision => {
              if (baseFarmSubdivisionYear && baseFileName) {
                farmSubdivision.templateFileName = baseFileName
              }
              return createFarmSubdivision({ farmSubdivision })
            }),
          )
        } finally {
          // No rollback: a failure mid-batch still mutated the server, so the cache is rebuilt
          // whether this resolved or rejected.
          await invalidateAllotmentReads()
        }

        resetControls()
      },
    })
  }

  const updateAllotment = async () => {
    await processLoading({
      setLoadingModalConfig,
      loadingMessage: 'Guardando lote...',
      doAction: async () => {
        // Single source of truth for the save partition — the pre-flight lock guard evaluates the
        // exact same split so the check and the mutations can never drift. Locked paddocks are
        // excluded from the update batch (they can never be edited through this UI) but their ids
        // are retained below so they are not silently dropped from the loteo.
        const {
          paddockIdsToDelete,
          paddocksFeaturesToUpdate,
          paddocksFeaturesToCreate,
          retainedLockedPaddockIds,
        } = partitionPaddocksForFullEditSave({
          selectedFarmSubdivision,
          paddocksFeatures,
          lockedPaddockIds,
        })

        try {
          await Promise.all(
            paddockIdsToDelete.map(paddockId =>
              deletePaddock({
                id: paddockId,
                userRole: userRoleSelected,
                farmId: farm.id,
              }),
            ),
          )

          await updatePaddocks({
            paddocks: paddocksFeaturesToUpdate,
            farmId: farm.id,
          })

          const paddocksToCreate = paddocksFeaturesToCreate.map(paddock => {
            return {
              name: paddock.properties.name,
              geometry: paddock.geometry,
              farmId: farm.id,
              isInProject: paddock.properties.isInProject,
            }
          })
          const paddocksCreated = await createPaddocks({
            farm,
            years: [selectedFarmSubdivision],
            paddocks: paddocksToCreate,
          })

          const paddockIdsCreated = paddocksCreated.flatMap(paddock => paddock.paddockIds)
          const paddockIdsToUpdated = paddocksFeaturesToUpdate.map(paddock => paddock.properties.id)
          const newSelectedFarmSubdivision = { ...selectedFarmSubdivision }
          newSelectedFarmSubdivision.paddockIds = paddockIdsCreated
            .concat(paddockIdsToUpdated)
            .concat(retainedLockedPaddockIds)
          await updateSubdivision({ farmSubdivision: newSelectedFarmSubdivision })
        } finally {
          // No rollback: a failure at any step still mutated the server, so the cache is rebuilt
          // either way. Deliberately not throwOnError — a rejection here would replace the failure
          // the caller actually needs to see.
          await invalidateAllotmentReads()
          // A delete that landed before the batch failed still dissociated its paddocks_usage
          // metrics, which useLockedPaddocks reads from that separate root.
          queryClient.invalidateQueries({ queryKey: queryKeys.metricEventsRoot })
        }

        resetControls()
      },
    })
  }

  // A completed v2 partial import is already persisted atomically server-side (spec: Atomic
  // Persistence), so reconcile it exactly like every other write in this panel: re-fetch the
  // authoritative loteo (reloadInformation) instead of patching created paddocks into local state
  // as if they were pending edits. This keeps the source of truth server-owned and avoids the
  // "Volver warns about losing already-saved work" trap.
  const finishImport = async outcome => {
    if (outcome.skippedNames.length) {
      enqueueSnackbar(
        `Se omitieron lotes que ya existían (duplicados): ${outcome.skippedNames.join(', ')}.`,
        { variant: 'info' },
      )
    }
    // usePartialImportKml's isImporting only covers the upload itself and is already false by the
    // time this runs (its finally block fires before the awaited call returns). Without
    // processLoading, the reload round trips would run with every editing control
    // live again, showing the pre-import snapshot — matching the other reloadInformation call
    // sites (saveAllotment, updateAllotment, handleDeleteFarmSubdivision, handleTemplateUpload)
    // closes that window: loadingModalConfig.open unmounts the whole editable body while true.
    await processLoading({
      setLoadingModalConfig,
      loadingMessage: 'Actualizando loteo...',
      errorMessage:
        'El archivo KML se importó correctamente, pero no se pudo actualizar la vista. Vuelva a ingresar al loteo para ver los cambios.',
      doAction: async () => {
        try {
          await reloadInformation()
        } catch (error) {
          // The import already committed server-side; leaving the stale session editable would
          // let a later full-edit save silently detach the imported paddocks.
          resetControls()
          throw error
        }
      },
    })
  }

  // Tagged backend failures name the offending lote and the reason; anything else is a
  // parse/network failure the operator can only act on by retrying with a different file, so its
  // raw English message is logged for diagnostics and never shown in this Spanish UI.
  const reportImportFailure = error => {
    console.error(error)
    enqueueSnackbar(
      getKmlImportErrorMessage(error) ||
        'No se pudo importar el archivo KML. Verifique el archivo e intente nuevamente.',
      { variant: 'error' },
    )
  }

  const reportRemovedByClip = removedNames => {
    if (!removedNames.length) return
    enqueueSnackbar(
      `Se descartaron lotes sin área restante tras el recorte: ${removedNames.join(', ')}.`,
      { variant: 'info' },
    )
  }

  const runImportAction = async importAction => {
    try {
      // A null outcome is an upload aborted by the hook (panel unmounted, or the loteo was
      // retargeted mid-flight); the backend write may still have committed, so there is nothing to
      // report and nothing to reconcile.
      const outcome = await importAction()
      if (outcome) await finishImport(outcome)
    } catch (error) {
      // The backend re-classifies overlaps inside its transaction, against paddocks that may have
      // appeared after this session took its snapshot — so a batch this panel clipped clean can
      // still come back rejected, naming the offending lotes. That is a recoverable state, not the
      // generic "the file is bad" failure.
      const conflictNames = getServerConflictFeatureNames(error)
      if (conflictNames.length) {
        setServerConflict({
          stagedIds: stagedImportFeatures
            .filter(staged => conflictNames.includes(staged.properties.name?.trim()))
            .map(staged => staged.properties.stagedId),
          names: conflictNames,
        })
        enqueueSnackbar(
          `El servidor rechazó la importación: ${conflictNames.join(', ')} se superponen con lotes existentes que cambiaron durante la edición. Actualice los lotes existentes y recorte para continuar.`,
          { variant: 'error' },
        )
        return
      }
      reportImportFailure(error)
    }
  }

  const handleImportKml = async e => {
    const [file] = e.target.files
    // Reset so re-selecting the same file still fires onChange after a rejected staging.
    e.target.value = ''
    if (!file) return
    // Staging REPLACES the working staging, so a second file would silently destroy every rename,
    // removal and clip already applied to the current one. The button is disabled for this reason;
    // this is the guard for anything that reaches the handler anyway.
    if (hasStagedImport) return
    // Staging is purely local — nothing is uploaded and no session work is discarded, so there is
    // nothing to confirm here.
    setServerConflict(noServerConflict)
    try {
      await stageKmlFile(file)
    } catch (error) {
      reportImportFailure(error)
    }
  }

  // Clipping only rewrites LOCAL staged state (existing paddocks never yield), so it needs no
  // unsaved-edits guard and no reload — only feedback for lotes that vanished entirely.
  const handleClipStaged = () => {
    reportRemovedByClip(clipStagedFeatures().removedNames)
  }

  // Recovery from a backend overlap rejection. Clipping again against the session's snapshot would
  // rebuild byte-identical geometry and earn the identical rejection — an inescapable loop — so the
  // current paddocks are re-read first and the staged lotes are clipped against THOSE. The raw
  // fetched geometry (not the perimeter-normalized working set) is what the backend classifies
  // against, so it is what the clip must yield to.
  const refreshPaddocksAndClipStaged = async () => {
    await processLoading({
      setLoadingModalConfig,
      loadingMessage: 'Actualizando lotes existentes...',
      errorMessage: 'No se pudieron actualizar los lotes existentes. Intente nuevamente.',
      doAction: async () => {
        // Deliberately not through the shared cache: a cached (or mid-invalidation) list is exactly
        // the race the override below exists to avoid.
        const [newFarmSubdivisions, newPaddocks] = await Promise.all([
          getFarmSubdivisions({ farmId: farm.id }),
          getPaddocks({ farmId: farm.id }),
        ])
        const newFarmSubdivision = newFarmSubdivisions.find(
          subdivision => subdivision.id === selectedFarmSubdivision.id,
        )
        if (!newFarmSubdivision) {
          throw new errors.RuutsError(
            `FarmSubdivision no longer exists: ${selectedFarmSubdivision.id}`,
          )
        }

        const currentPaddockFeatures = newPaddocks
          .filter(paddock => newFarmSubdivision.paddockIds.includes(paddock.id))
          .map(paddock => ({ ...paddock.toGeoJSON, metrics: paddock.metrics }))

        // Deliberately NOT written back into the farm-wide paddocks / farmSubdivisions state: that
        // re-runs the base-loteo effect, whose else-branch clears paddocksFeatures — the working set
        // this action exists to refresh. Only the loteo's own working set and membership are
        // updated; allowEdition stays as this session resolved it (monitoring sites included) rather
        // than being silently re-derived mid-edit, and fresh paddockIds keep the full-edit save
        // partition from dropping a paddock added meanwhile.
        const normalizedPaddockFeatures = normalizePaddockFeatures(currentPaddockFeatures)
        setPaddocksFeatures(normalizedPaddockFeatures)
        setSelectedFarmSubdivision(current => ({
          ...current,
          paddockIds: newFarmSubdivision.paddockIds,
        }))
        // The refreshed set is what this session now starts from, so the lock guard's baseline moves
        // with it. The normalization above legitimately reshapes paddocks — a locked one included —
        // against a paddock another operator added meanwhile, with zero user action; comparing that
        // against the session-start snapshot would read it as a user edit and block the save for
        // good. Captured post-normalization, as the guard's contract requires.
        setPaddockGeometrySnapshot(capturePaddockGeometrySnapshot(normalizedPaddockFeatures))
        // The working set was replaced wholesale, so whatever it held is gone — the confirmIfDirty
        // guard on this action is what makes that acceptable.
        setHasUnsavedEdits(false)

        reportRemovedByClip(clipStagedFeatures(currentPaddockFeatures).removedNames)
        setServerConflict(noServerConflict)
      },
    })
  }

  const handleRefreshPaddocksAndClipStaged = () => {
    confirmIfDirty({
      isDirty: hasUnsavedEdits,
      message:
        'Si actualiza los lotes existentes ahora, se perderán los cambios sin guardar de esta edición. ¿Desea continuar?',
      onConfirm: refreshPaddocksAndClipStaged,
    })
  }

  const handleConfirmImport = async () => {
    // finishImport re-fetches the loteo (reloadInformation), which exits edit mode — so
    // confirming while real edits are unsaved would silently discard them. Ask first, exactly
    // like Volver does — but gated on hasUnsavedEdits, not the broader partialChanges (see its
    // declaration).
    confirmIfDirty({
      isDirty: hasUnsavedEdits,
      message:
        'Si confirma la importación ahora, se perderán los cambios sin guardar de esta edición. ¿Desea continuar?',
      onConfirm: async () => runImportAction(confirmImport),
    })
  }

  const handleUpdateAllotment = async () => {
    // Pre-flight lock check — block the whole save client-side before any of the 4 mutation calls
    // fire when a locked paddock would be deleted, or when its geometry was silently rewritten by
    // a map draw/edit clip (checkIntersectionWithOtherFeatures clips whatever existing polygon a
    // new/edited one overlaps, locked or not, carrying the id over unchanged — no row interaction
    // needed), so neither path can commit ahead of a server rejection.
    const { paddockIdsToDelete } = partitionPaddocksForFullEditSave({
      selectedFarmSubdivision,
      paddocksFeatures,
      lockedPaddockIds,
    })
    const lockedPaddocksTouched = findLockedPaddocksTouchedByFullEditSave({
      paddockIdsToDelete,
      paddocksFeatures,
      geometrySnapshot: paddockGeometrySnapshot,
      lockedPaddockIds,
    })
    if (lockedPaddocksTouched.length) {
      // Surface paddock names, not raw ids. A deleted paddock is gone from paddocksFeatures by
      // definition, so look it up from the fetched paddocks list instead; only fall back to the
      // raw id if the app genuinely has no record of it (defensive, should not happen in practice).
      const lockedNames = lockedPaddocksTouched.map(
        id =>
          paddocks.find(paddock => paddock.id?.toLowerCase() === id.toLowerCase())?.toGeoJSON
            ?.properties?.name ?? id,
      )
      // The Importar KML button only renders for partial-import programs — elsewhere the advice
      // would point at a control that does not exist.
      const kmlAdvice = partialImportEnabled
        ? ' Use la importación de KML para agregar lotes sin modificar los bloqueados.'
        : ''
      enqueueSnackbar(
        `No se puede guardar: la edición afecta a un lote bloqueado (${lockedNames.join(', ')}).${kmlAdvice}`,
        { variant: 'error' },
      )
      return
    }

    // A loteo the list no longer carries (or has not resolved yet) has no allowEdition, and the
    // confirm-anyway prompt is the safe branch to fall into.
    if (!selectedFarmSubdivision?.allowEdition?.value) {
      setConfirmationModalConfig({
        open: true,
        title: '¿Está seguro de editar el loteo?',
        message: selectedFarmSubdivision?.allowEdition?.reason,
        confirmLabel: 'Continuar de todas formas',
        cancelLabel: 'Cancelar',
        confirmAction: () => {
          updateAllotment()
        },
      })
      return
    }

    updateAllotment()
  }

  useEffect(() => {
    if (farmSubdivisions.length) {
      const years = farmSubdivisions.map(subdivision => subdivision.year)
      setSavedYears(years)
    }
  }, [farmSubdivisions])

  useEffect(() => {
    const hasSelectedYears = selectedYears.length > 0
    setPartialChanges(hasSelectedYears && drawActivated)
    if (selectedYears.length !== 1 || drawActivated) return

    const found = farmSubdivisions.find(subdivision => subdivision.year === selectedYears[0])
    if (!found) return
    // The LP loteo defines the project area a SOC randomization was drawn against, so it stays
    // frozen only while LIVE random sites exist — the same criterion the save-time gate
    // (disableActionReason) uses. A backend-supplied block wins: its reason is more specific.
    const blocksLpEdition =
      found.year === LAND_PLAN_YEAR && hasRandomMonitoringSites && found.allowEdition?.value
    const newSelectedFarmSubdivision = blocksLpEdition
      ? {
          ...found,
          allowEdition: {
            value: false,
            reason: messages.getMessage(
              messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_MONITORING_SITES_YEAR_MESSAGE,
            ),
          },
        }
      : found
    setSelectedFarmSubdivision(newSelectedFarmSubdivision)

    const signature = workingSetSignature(newSelectedFarmSubdivision)
    if (seededWorkingSetRef.current === signature) return
    // Defense in depth behind the drawActivated gate above: a future entry point that drops that
    // invariant still cannot silently discard session work.
    if (seededWorkingSetRef.current !== null && hasSessionWorkToLose) return

    const paddocksFromSubdivision = newSelectedFarmSubdivision.paddockIds ?? []

    const selectedPaddocks = paddocks.filter(paddock =>
      paddocksFromSubdivision.includes(paddock.id),
    )
    if (
      !canSeedWorkingSet({
        farm,
        paddockIds: paddocksFromSubdivision,
        resolvedPaddocks: selectedPaddocks,
      })
    )
      return
    seededWorkingSetRef.current = signature

    selectedPaddocks.forEach(p => {
      p.toGeoJSON.properties.area = GeoJSON.hectareArea(p.toGeoJSON)
    })
    handleIntersections(
      selectedPaddocks.map(paddock => ({ ...paddock.toGeoJSON, metrics: paddock.metrics })),
    )
  }, [
    drawActivated,
    farm,
    farmSubdivisions,
    handleIntersections,
    hasSessionWorkToLose,
    paddocks,
    selectedYears,
    setPartialChanges,
    hasRandomMonitoringSites,
  ])

  useEffect(() => {
    if (!baseFarmSubdivisionYear) {
      // Only an actual deselection empties the working set: this effect also re-runs whenever the
      // server lists change identity, and clearing there would destroy the create session's work.
      if (previousBaseYearRef.current) {
        previousBaseYearRef.current = ''
        clearWorkingSet()
      }
      return
    }
    // An explicit base-loteo pick replaces the working set even when there is session work to lose;
    // the same effect firing because the server list changed identity does not.
    const baseYearChanged = previousBaseYearRef.current !== baseFarmSubdivisionYear
    previousBaseYearRef.current = baseFarmSubdivisionYear

    const baseFarmSubdivision = farmSubdivisions.find(
      subdivision => subdivision.year === baseFarmSubdivisionYear,
    )
    if (!baseFarmSubdivision) return

    const signature = workingSetSignature(baseFarmSubdivision)
    if (seededWorkingSetRef.current === signature) return
    if (!baseYearChanged && seededWorkingSetRef.current !== null && hasSessionWorkToLose) return

    const paddocksFromSubdivision = baseFarmSubdivision.paddockIds ?? []

    const selectedPaddocks = paddocks.filter(paddock =>
      paddocksFromSubdivision.includes(paddock.id),
    )
    if (
      !canSeedWorkingSet({
        farm,
        paddockIds: paddocksFromSubdivision,
        resolvedPaddocks: selectedPaddocks,
      })
    )
      return
    seededWorkingSetRef.current = signature

    selectedPaddocks.forEach(p => {
      p.toGeoJSON.properties.area = GeoJSON.hectareArea(p.toGeoJSON)
    })

    handleIntersections(selectedPaddocks.map(paddock => paddock.toGeoJSON))
    setPartialChanges(true)
  }, [
    baseFarmSubdivisionYear,
    clearWorkingSet,
    farm,
    farmSubdivisions,
    handleIntersections,
    hasSessionWorkToLose,
    paddocks,
    setPartialChanges,
  ])

  const handleBack = () => {
    // Entering a map Edit/Draw/Cancelled state resets partialChanges while real session edits
    // (tracked by hasUnsavedEdits) are still pending — either flag alone under-reports dirtiness.
    confirmIfDirty({
      isDirty: partialChanges || hasUnsavedEdits,
      message: 'Si confirma volver, se perderán los cambios. ¿Desea continuar?',
      onConfirm: resetControls,
    })
  }

  const disableActionReason = useMemo(() => {
    // An empty lockedPaddockIds is indistinguishable from "confirmed nothing is locked" — while
    // still loading or after a failed lookup, the guard below would fail OPEN (treat everything as
    // unlocked) instead of blocking, letting unlocked deletes commit ahead of a locked paddock the
    // app just hasn't heard about yet. Only relevant to reallotment — saveAllotment (create) never
    // consults lock data, so the lookup must never block loteo creation.
    if (reallotment && isLoadingLocks) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_VERIFYING_LOCKED_PADDOCKS_MESSAGE,
      )
    }
    if (reallotment && isLockLookupFailed) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_LOCKED_PADDOCKS_LOOKUP_FAILED_MESSAGE,
      )
    }

    // usePartialImportKml manages its own loading state instead of the shared loadingModalConfig,
    // so a full-edit save (partitioned against pre-import data) is otherwise free to fire and race
    // an in-flight import — whichever write lands last silently drops the other's paddocks.
    if (isImporting) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_KML_IMPORT_IN_PROGRESS_MESSAGE,
      )
    }

    // A pending staging (conflicted or not) must be confirmed or discarded before a full-edit
    // save — the save partitions the current working set, which a later confirm would race.
    if (hasStagedImport) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_PENDING_IMPORT_MESSAGE,
      )
    }

    if (paddocksFeatures.length === 0) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_EMPTY_GEOMETRIES_MESSAGE,
      )
    }

    if (paddocksFeatures.some(s => s.properties.name === '')) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_EMPTY_GEOMETRY_NAME_MESSAGE,
      )
    }

    if (
      !(isAdmin || isGISTechnician) &&
      hasRandomMonitoringSites &&
      selectedYears.includes(LAND_PLAN_YEAR)
    ) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_MONITORING_SITES_YEAR_MESSAGE,
      )
    }

    if (
      paddocksFeatures.some(
        (s, i) => paddocksFeatures.findIndex(s2 => s2.properties.name === s.properties.name) !== i,
      )
    ) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_DUPLICATE_NAMES_NAME_MESSAGE,
      )
    }

    if (selectedYears.length === 0) {
      return messages.getMessage(
        messages.keys.ERROR_CANNOT_EDIT_FARM_SUBDIVISION_YEAR_NON_SELECTED_MESSAGE,
      )
    }

    if (mapAction === mapActions.Edit) {
      return messages.getMessage(messages.keys.ERROR_CANNOT_EDIT_GEOMETRY_APPLY_CHANGES_MESSAGE)
    }

    if (isViewOnly && !isGISTechnician) {
      return messages.getMessage(messages.keys.NO_PERMISSIONS_GRANTED)
    }

    return null
  }, [
    paddocksFeatures,
    mapAction,
    selectedYears,
    isViewOnly,
    hasRandomMonitoringSites,
    isAdmin,
    isGISTechnician,
    hasStagedImport,
    isImporting,
    isLoadingLocks,
    isLockLookupFailed,
    reallotment,
  ])

  useEffect(() => {
    if (mapAction) {
      const isRealEdit = [
        mapActions.Drawn,
        mapActions.Edited,
        mapActions.ConfirmedModification,
        mapActions.Removed,
      ].includes(mapAction)
      if (isRealEdit) {
        markUnsavedEdit()
      } else {
        setPartialChanges(false)
      }
    }
  }, [mapAction, markUnsavedEdit, setPartialChanges])

  async function getPaddockMetrics(paddockId) {
    try {
      return await getMetricsByPaddockId({ paddockId })
    } catch (error) {
      throw new errors.RuutsError(`Unable to get metrics for paddock id: ${paddockId}`)
    }
  }

  async function handleDeletePaddock({ paddock }) {
    const paddockMetrics = paddock?.properties?.id
      ? await getPaddockMetrics(paddock.properties.id)
      : []

    const getDataCollectionCategories = ({ metrics }) => {
      const namespaces = metrics?.map(
        metric =>
          formDefinitions?.dataCollection?.find(
            formDefinition => formDefinition.namespace === metric.namespace,
          )?.label,
      )
      return namespaces.reduce((acc, cur) => {
        if (!acc.includes(cur)) {
          acc = [...acc, cur]
        }
        return acc
      }, [])
    }

    const deleteMessageConfirmation = ({ paddockMetrics }) => {
      if (!paddockMetrics?.length) return null

      const dcNamespaces = getDataCollectionCategories({ metrics: paddockMetrics })
      return (
        <>
          <Box sx={{}}>
            <Typography
              sx={{
                fontSize: 16,
              }}
            >
              <strong>Advertencia</strong>: El lote posee datos anualizados asociados.
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
              }}
            >
              Se borrará el lote de cada dato asociado en las pestañas:
            </Typography>
          </Box>
          <List dense>
            {dcNamespaces.map(namespace => (
              <ListItem key={namespace}>
                <ListItemIcon style={{ minWidth: '25px' }}>
                  <Avatar sx={{ width: 14, height: 14, marginRight: 0 }}>
                    <CircleIcon style={{ color: 'black' }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary={namespace} />
              </ListItem>
            ))}
          </List>
        </>
      )
    }

    setConfirmationModalConfig({
      open: true,
      modalType: ModalTypes.DeleteModalType,
      title: `Eliminar lote ${paddock.properties.name}`,
      message: '¿Está seguro de eliminar el lote? ',
      confirmAction: () => {
        setPaddocksFeatures(prev => {
          const newPaddocks = prev.filter(s => s !== paddock)
          return newPaddocks
        })
        setMapAction(mapActions.Removed)
      },
      children: deleteMessageConfirmation({ paddockMetrics }),
    })
  }

  const handleDeleteFarmSubdivision = async () => {
    setConfirmationModalConfig({
      modalType: ModalTypes.DeleteModalType,
      open: true,
      title: `Eliminar loteo ${selectedFarmSubdivision.year}`,
      message: `¿Está seguro de eliminar el loteo completo del año ${selectedFarmSubdivision.year}?`,
      confirmAction: async () =>
        processLoading({
          setLoadingModalConfig,
          loadingTitle: `Eliminando Loteo Año ${selectedFarmSubdivision.year}`,
          errorMessage: 'Error al eliminar el loteo.',
          displayErrorMessageMode: displayErrorMessageModes.dialog,
          doAction: async () => {
            await deleteSubdivision({
              id: selectedFarmSubdivision.id,
              deletePaddocks: true,
              farmId: farm.id,
            })
            await reloadInformation()
          },
        }),
    })
  }

  const handleTemplateUpload = async e => {
    const [file] = e.target.files
    if (!file) return
    await processLoading({
      setLoadingModalConfig,
      loadingTitle: 'Cargando archivo',
      doAction: async () => {
        await uploadSubdivisionTemplate({ file, farmId: farm.id })
        await reloadInformation()
      },
    })
  }

  const filteredYearsAllowed = useMemo(() => {
    if (!yearsAllowed) return []
    const lpAlreadyExists = savedYears.includes(LAND_PLAN_YEAR)
    if (
      programConfig?.lpRequiresNoMonitoringSites &&
      hasRandomMonitoringSites &&
      !lpAlreadyExists
    ) {
      return yearsAllowed.filter(year => year !== LAND_PLAN_YEAR)
    }
    return yearsAllowed
  }, [
    yearsAllowed,
    programConfig?.lpRequiresNoMonitoringSites,
    hasRandomMonitoringSites,
    savedYears,
  ])

  const displayInProject =
    selectedYears?.length === 1 &&
    // TODO: REMOVE TEMPLATE AFTER THE ONGOING FARMS ARE PROCESSED
    (selectedYears?.[0] === LAND_PLAN_YEAR || selectedYears?.[0] === 'TEMPLATE')

  return (
    <>
      {(loadingModalConfig.open || !farm) && (
        <Stack
          sx={{
            height: drawActivated ? '75vh' : '85vh',
            overflowY: 'scroll',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Stack>
      )}

      {!loadingModalConfig.open && farm && (
        <Stack sx={{ height: drawActivated ? '75vh' : '85vh', overflowY: 'scroll' }}>
          {!drawActivated && (
            <>
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                {wasSaved && (
                  <CustomAlert
                    message="Los cambios se guardaron correctamente."
                    severity="success"
                    title="Éxito"
                  />
                )}
                {!wasSaved && (
                  <CustomAlert
                    message="Un Loteo es la disposición de los Lotes en un momento determinado."
                    severity="info"
                    title="¿Sabías qué?"
                  />
                )}
              </Stack>
              <Stack sx={{ marginTop: 2, padding: '20px', textAlign: 'center' }}>
                <Typography variant="h6">Loteo</Typography>

                <Typography variant="p">
                  Cree un Loteo y defina al período que corresponde.
                </Typography>

                <Stack spacing={2} sx={{ marginTop: 2, width: '100%' }}>
                  {filteredYearsAllowed.includes('TEMPLATE') && (
                    <Button
                      color="primary"
                      component="label"
                      disabled={isViewOnly}
                      endIcon={<UploadFile />}
                      sx={{ width: '100%' }}
                      variant="outlined"
                    >
                      CARGAR LOTEO BASE (KML)
                      <input
                        hidden
                        accept=".kml"
                        id="kml-upload-button"
                        type="file"
                        onChange={handleTemplateUpload}
                      />
                    </Button>
                  )}
                  <Button
                    color="primary"
                    disabled={isViewOnly}
                    endIcon={<AddIcon />}
                    sx={{ width: '100%' }}
                    variant="contained"
                    onClick={() => {
                      setSelectedYears([])
                      setSelectedFarmSubdivision({})
                      clearWorkingSet()
                      setDrawActivated(true)
                      setReallotment(false)
                    }}
                  >
                    Crear nuevo loteo
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
          {!drawActivated && farmSubdivisions.length > 0 && (
            <Box sx={{ padding: '20px' }}>
              <Divider />
              <YearSelector
                multiselectYears={false}
                savedYears={savedYears}
                selectedYears={selectedYears}
                setSelectedYears={setSelectedYears}
                title="Loteos cargados"
                years={filteredYearsAllowed}
              />
            </Box>
          )}
          {!drawActivated &&
            paddocksFeatures.length > 0 &&
            farmSubdivisions.length > 0 &&
            selectedYears.length > 0 && (
              <>
                <Typography sx={{ textAlign: 'center' }} variant="h6">
                  Loteo {selectedYears[0]}
                </Typography>
                {baseFileName && (
                  <Typography sx={{ textAlign: 'left', padding: '10px 50px' }} variant="subtitle2">
                    <b>Archivo base:</b> {baseFileName}
                  </Typography>
                )}
                <Box sx={{ padding: '20px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Área</TableCell>
                        {displayInProject && (
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {currentFarm.programId === PROGRAM_IDS.SARA
                              ? 'Loteos ganaderos con MH'
                              : 'Incluir en proyecto'}
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paddocksFeatures.map((paddock, index) => (
                        <TableRow key={paddock.properties.id || paddock.properties.name || index}>
                          <TableCell>{paddock.properties.name}</TableCell>
                          <TableCell sx={{ textAlign: 'end' }}>
                            {paddock.properties.area.toFixed(2)} ha
                          </TableCell>
                          {displayInProject && (
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Checkbox
                                disabled
                                checked={paddock.properties.isInProject}
                                size="small"
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                <ButtonGroup fullWidth sx={{ padding: '20px', width: '100%' }}>
                  <EditionButton
                    color="error"
                    disabled={isEditDisabled}
                    endIcon={<Delete />}
                    showTooltip={isEditDisabled && Boolean(editDisabledReason)}
                    tooltipTitle={editDisabledReason}
                    onClick={handleDeleteFarmSubdivision}
                  >
                    Eliminar Loteo
                  </EditionButton>
                  <EditionButton
                    disabled={isEditDisabled}
                    showTooltip={isEditDisabled && Boolean(editDisabledReason)}
                    tooltipTitle={editDisabledReason}
                    onClick={() => {
                      setPaddockGeometrySnapshot(capturePaddockGeometrySnapshot(paddocksFeatures))
                      setPartialChanges(false)
                      setDrawActivated(true)
                      setReallotment(true)
                    }}
                  >
                    Editar Loteo
                  </EditionButton>
                </ButtonGroup>
              </>
            )}
          {drawActivated && (
            <>
              <Box sx={{ padding: '10px' }}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    cursor: isImporting ? 'default' : 'pointer',
                    opacity: isImporting ? 0.4 : 1,
                    pointerEvents: isImporting ? 'none' : 'auto',
                  }}
                  onClick={() => {
                    // Confirming Volver mid-upload would abandon a possibly-committed atomic
                    // import with no reconciliation — a later full-edit save would silently
                    // detach the imported paddocks.
                    if (isImporting) return
                    handleBack()
                  }}
                >
                  <IconButton aria-label="Volver" disabled={isImporting}>
                    <ArrowBackIosIcon />
                  </IconButton>
                  <Typography sx={{ color: 'rgba(0, 0, 0, 0.54)' }} variant="p">
                    <b>Volver</b>
                  </Typography>
                  <Typography sx={{ marginLeft: 'auto' }} variant="h6">
                    <b>
                      {reallotment ? `Edición de Loteo ${selectedYears[0]}` : 'Creación de Loteo'}
                    </b>
                  </Typography>
                </Stack>
              </Box>
              <Grid container sx={{ padding: '0 10px' }}>
                <Grid item sx={{ textAlign: 'center' }} xs={12} />
                {!reallotment && (
                  <>
                    <Grid item sx={{ textAlign: 'center' }} xs={12}>
                      <YearSelector
                        multiselectYears
                        savedYears={savedYears}
                        selectedYears={selectedYears}
                        setSelectedYears={setSelectedYears}
                        title="Seleccione el período del loteo a crear"
                        years={filteredYearsAllowed.filter(year => year !== 'TEMPLATE')}
                      />
                    </Grid>
                    <Grid item sx={{ textAlign: 'center' }} xs={12}>
                      <BaseFarmSubdivisionYearSelect
                        baseFarmSubdivisionYear={baseFarmSubdivisionYear}
                        baseFileName={baseFileName}
                        savedYears={savedYears}
                        selectedYears={selectedYears}
                        setBaseFarmSubdivisionYear={setBaseFarmSubdivisionYear}
                        years={filteredYearsAllowed}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Divider />
                  <Box
                    sx={{
                      padding: '10px',
                      justifyContent: 'center',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    <Button
                      disabled={
                        mapAction === mapActions.Edit ||
                        selectedYears.length === 0 ||
                        isViewOnly ||
                        isEditingBlocked
                      }
                      size="small"
                      startIcon={<AddIcon />}
                      variant="outlined"
                      onClick={() => {
                        setSelectedArea(null)
                        setSelectedAreaType('paddock')
                        setMapAction(mapActions.Draw)
                      }}
                    >
                      Crear Lote
                    </Button>
                    {partialImportEnabled && reallotment && (
                      <Button
                        color="primary"
                        component="label"
                        disabled={
                          mapAction === mapActions.Edit ||
                          isEditingBlocked ||
                          isViewOnly ||
                          hasStagedImport
                        }
                        endIcon={<UploadFile />}
                        size="small"
                        variant="outlined"
                      >
                        Importar KML
                        {/* KMZ is a zipped KML the client parser (raw XML text) cannot read —
                            advertising it would promise a format the staging flow does not
                            actually support. */}
                        <input hidden accept=".kml" type="file" onChange={handleImportKml} />
                      </Button>
                    )}
                    {displayInProject && paddocksFeatures.length > 0 && (
                      <Button
                        color="info"
                        disabled={mapAction === mapActions.Edit || isEditingBlocked}
                        size="small"
                        startIcon={allSelected ? <CheckBox /> : <CheckBoxOutlineBlank />}
                        variant="text"
                        onClick={handleSelectAll}
                      >
                        <Typography variant="inherit">
                          {currentFarm.programId === PROGRAM_IDS.SARA
                            ? allSelected
                              ? 'Quitar lotes ganaderos con MH'
                              : 'Incluir lotes ganaderos con MH'
                            : allSelected
                              ? 'Quitar todo del proyecto'
                              : 'Incluir todo al proyecto'}
                        </Typography>
                      </Button>
                    )}
                  </Box>
                </Grid>
                {partialImportEnabled && reallotment && hasStagedImport && (
                  <Grid item xs={12}>
                    <Box
                      aria-label="Lotes importados"
                      role="group"
                      sx={{
                        margin: '10px',
                        padding: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, textAlign: 'center' }} variant="subtitle2">
                        Lotes importados
                      </Typography>
                      {stagedImportFeatures.map(staged => (
                        <Stack
                          key={staged.properties.stagedId}
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center' }}
                        >
                          <TextField
                            disabled={isImporting}
                            error={
                              invalidStagedIds.has(staged.properties.stagedId) ||
                              duplicateStagedIdSet.has(staged.properties.stagedId)
                            }
                            placeholder="Nombre"
                            sx={{ flexGrow: 1 }}
                            value={staged.properties.name}
                            variant="standard"
                            onChange={e =>
                              renameStagedFeature(staged.properties.stagedId, e.target.value)
                            }
                          />
                          <Typography variant="body2">
                            {staged.properties.area?.toFixed(2)} ha
                          </Typography>
                          <DeleteIconButton
                            aria-label={`Quitar lote importado ${staged.properties.name || ''}`.trim()}
                            disabled={isImporting}
                            onClick={() => removeStagedFeature(staged.properties.stagedId)}
                          />
                        </Stack>
                      ))}
                      {hasDuplicateGeometries && (
                        <Typography color="error" sx={{ textAlign: 'center' }} variant="body2">
                          {`Se detectaron ${duplicateStagedIds.length} lotes con la misma geometría que otro lote del archivo. Quite las copias repetidas, dejando una de cada lote, para continuar.`}
                        </Typography>
                      )}
                      {hasConflict && (
                        <Typography color="error" sx={{ textAlign: 'center' }} variant="body2">
                          Los siguientes lotes se superponen con lotes existentes:{' '}
                          {conflictingFeatureNames.join(', ')}. Recórtelos antes de confirmar.
                        </Typography>
                      )}
                      {hasServerConflict && (
                        <Typography color="error" sx={{ textAlign: 'center' }} variant="body2">
                          El servidor rechazó la importación: {serverConflictNames.join(', ')} se
                          superponen con lotes existentes que cambiaron durante la edición.
                          Actualice los lotes existentes y recorte para continuar.
                        </Typography>
                      )}
                      {hasInvalidStagedNames && (
                        <Typography color="error" sx={{ textAlign: 'center' }} variant="caption">
                          Todos los lotes importados deben tener un nombre, distinto entre sí y
                          distinto del de los lotes ya existentes en el loteo.
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        sx={{ justifyContent: 'center', flexWrap: 'wrap', gap: '8px' }}
                      >
                        {/* One clip affordance, two sources of truth: the local working set is
                            enough for a conflict this session can see, but a conflict only the
                            backend saw can only be resolved against re-read paddocks. */}
                        <Button
                          color="warning"
                          disabled={mapAction === mapActions.Edit || isImporting}
                          size="small"
                          variant="outlined"
                          onClick={
                            hasServerConflict
                              ? handleRefreshPaddocksAndClipStaged
                              : handleClipStaged
                          }
                        >
                          {hasServerConflict
                            ? 'Actualizar lotes y recortar'
                            : 'Recortar contra lotes existentes'}
                        </Button>
                        <Button
                          color="primary"
                          disabled={
                            mapAction === mapActions.Edit ||
                            isEditingBlocked ||
                            hasConflict ||
                            hasServerConflict ||
                            hasInvalidStagedNames ||
                            hasDuplicateGeometries
                          }
                          size="small"
                          variant="contained"
                          onClick={handleConfirmImport}
                        >
                          Confirmar importación
                        </Button>
                        {/* Staging performed zero server writes, so discarding is purely local —
                            the clip/confirm pair must never be the only way out of the
                            save-blocking staged state. */}
                        <Button
                          color="inherit"
                          disabled={isImporting}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            discardStagedImport()
                            setServerConflict(noServerConflict)
                          }}
                        >
                          Descartar importación
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                )}
                {paddocksFeatures.length > 0 &&
                  paddocksFeatures.map((paddock, index) => {
                    return (
                      <Grid
                        key={paddock.properties.id || paddock.properties.name || index}
                        item
                        xs={12}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{
                            alignItems: 'center',
                            marginTop: '10px',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
                            {isPaddockLocked(paddock) ? (
                              <Stack alignItems="center" direction="row" spacing={1}>
                                <Typography sx={{ fontWeight: 600 }} variant="body2">
                                  {paddock.properties.name}
                                </Typography>
                                <Chip
                                  color="warning"
                                  label="Bloqueado"
                                  size="small"
                                  variant="outlined"
                                />
                              </Stack>
                            ) : (
                              <TextField
                                disabled={isEditingBlocked}
                                error={
                                  paddocksFeatures.filter(
                                    s => s.properties.name === paddock.properties.name,
                                  ).length > 1 || !paddock.properties.name
                                }
                                placeholder="Nombre"
                                value={paddock.properties.name}
                                variant="standard"
                                onChange={e => {
                                  setPaddocksFeatures(prev =>
                                    prev.map((p, i) =>
                                      i === index
                                        ? {
                                            ...p,
                                            properties: { ...p.properties, name: e.target.value },
                                          }
                                        : p,
                                    ),
                                  )
                                  markUnsavedEdit()
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="p">
                            {paddock.properties?.area?.toFixed(2)} ha
                          </Typography>
                          {displayInProject && (
                            <FormControl
                              component="fieldset"
                              size="small"
                              sx={{ m: 1, minWidth: 50 }}
                            >
                              <Box alignItems="center" display="flex" flexDirection="column">
                                <Checkbox
                                  checked={paddock.properties.isInProject || false}
                                  disabled={isEditingBlocked || isPaddockLocked(paddock)}
                                  name="isInProject"
                                  onChange={e => {
                                    setPaddocksFeatures(prev =>
                                      prev.map((p, i) =>
                                        i === index
                                          ? {
                                              ...p,
                                              properties: {
                                                ...p.properties,
                                                isInProject: e.target.checked,
                                              },
                                            }
                                          : p,
                                      ),
                                    )
                                    markUnsavedEdit()
                                  }}
                                />
                                <FormLabel
                                  component="legend"
                                  style={{ textAlign: 'center', width: '100%' }}
                                >
                                  {currentFarm.programId === PROGRAM_IDS.SARA
                                    ? 'Loteo ganaderos con MH'
                                    : 'Incluir en proyecto'}
                                </FormLabel>
                              </Box>
                            </FormControl>
                          )}
                          {!isPaddockLocked(paddock) && (
                            <Stack direction="row" spacing={0} sx={{ alignItems: 'center' }}>
                              {mapAction === mapActions.Edit && selectedArea === paddock ? (
                                <>
                                  <IconButton
                                    aria-label={`Confirmar edición de ${paddock.properties.name}`}
                                    color="primary"
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                      setSelectedArea(paddock)
                                      setSelectedAreaType('paddock')
                                      setMapAction(mapActions.ConfirmedModification)
                                    }}
                                  >
                                    <DoneIcon />
                                  </IconButton>
                                  <IconButton
                                    aria-label={`Cancelar edición de ${paddock.properties.name}`}
                                    color="primary"
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                      setSelectedArea(paddock)
                                      setSelectedAreaType('paddock')
                                      setMapAction(mapActions.Cancelled)
                                    }}
                                  >
                                    <ClearOutlinedIcon />
                                  </IconButton>
                                </>
                              ) : (
                                <EditIconButton
                                  key={`edit-${paddock.properties.id}`}
                                  aria-label={`Editar geometría de ${paddock.properties.name}`}
                                  disabled={
                                    (mapAction === mapActions.Edit && selectedArea !== paddock) ||
                                    (isViewOnly && !isGISTechnician) ||
                                    isEditingBlocked
                                  }
                                  onClick={() => {
                                    setSelectedArea(paddock)
                                    setSelectedAreaType('paddock')
                                    setMapAction(mapActions.Edit)
                                  }}
                                />
                              )}
                              <DeleteIconButton
                                key={`delete-${paddock.properties.id}`}
                                aria-label={`Eliminar lote ${paddock.properties.name}`}
                                disabled={(isViewOnly && !isGISTechnician) || isEditingBlocked}
                                sx={{
                                  display:
                                    mapAction === mapActions.Edit && selectedArea === paddock
                                      ? 'none'
                                      : 'initial',
                                }}
                                onClick={() => {
                                  handleDeletePaddock({ paddock })
                                }}
                              />
                            </Stack>
                          )}
                        </Stack>
                      </Grid>
                    )
                  })}
              </Grid>
            </>
          )}
        </Stack>
      )}
      {drawActivated && paddocksFeatures && !reallotment && (
        <Box sx={{ paddingTop: '20px', width: '100%', textAlign: 'center' }}>
          <LoadingButton
            disableActionReason={disableActionReason}
            loading={loadingModalConfig.open}
            onClick={saveAllotment}
          >
            Guardar Loteo
          </LoadingButton>
        </Box>
      )}
      {drawActivated && paddocksFeatures && reallotment && (
        <Box sx={{ paddingTop: '20px', width: '100%', textAlign: 'center' }}>
          <LoadingButton
            disableActionReason={disableActionReason}
            loading={loadingModalConfig.open}
            onClick={handleUpdateAllotment}
          >
            Actualizar Loteo
          </LoadingButton>
        </Box>
      )}
    </>
  )
}

export default AllotmentPanel
